import { fetch as expoFetch } from "expo/fetch";
import { useCallback, useEffect, useState, useTransition } from "react";
import { Platform } from "react-native";
import { DEMO_SESSION_ANALYSIS_ENDPOINT } from "../../qa-search";
import { ENDPOINT_SESSION_ANALYSIS } from "../constants";
import { type DemoExamsType, mockedSessionAnalysis } from "./constants";

type GetAuthHeaders = () => Promise<Record<string, string>>;
type FetchResponse = Awaited<ReturnType<typeof expoFetch>>;

const streamTextResponse = async (
	response: FetchResponse,
	onText: (text: string) => void,
	signal: AbortSignal,
) => {
	const reader = await response.body?.getReader();
	if (!reader) {
		return;
	}
	const decoder = new TextDecoder();
	let result = "";
	let done = false;
	while (!done) {
		const { value, done: streamDone } = await reader.read();
		done = streamDone;
		if (signal.aborted) {
			await reader.cancel();
			return;
		}
		if (value) {
			result += decoder.decode(value, { stream: !done });
			onText(result);
		}
	}
};

type Params = {
	sessionId: number;
	examName: string;
	tenantName: string;
	outputLanguage?: string;
	demoExamId: string;
	examId?: string | number;
	appId?: string;
	getAuthHeaders?: GetAuthHeaders;
	userData?: Record<string, unknown>;
	goldStandardData?: Record<string, unknown>;
	ready?: boolean;
	additionalContext?: string;
};

export const useSessionAnalysis = ({
	sessionId,
	examName,
	tenantName,
	outputLanguage,
	demoExamId,
	examId,
	appId,
	getAuthHeaders,
	userData,
	goldStandardData,
	ready = true,
	additionalContext,
}: Params) => {
	const [sessionAnalysis, setSessionAnalysis] = useState("");
	const [loading, setLoading] = useState(false);
	const [streaming, setStreaming] = useState(false);
	const [_isPending, startTransition] = useTransition();
	const isMediQuest = demoExamId === "mediquest-1";
	// A hub appId is only supplied for live modules (ProEthica); its presence
	// routes the real /api/v2/ai/sessionAnalysis agent server-side. Everything
	// else keeps the existing mocked/demo behavior.
	const isLiveAnalysis = Boolean(appId);

	const postLiveAnalysis = async (signal: AbortSignal) => {
		const authHeaders = getAuthHeaders ? await getAuthHeaders() : {};
		const response = await expoFetch(ENDPOINT_SESSION_ANALYSIS, {
			method: "POST",
			signal,
			headers: {
				"Content-Type": "application/json",
				Accept: "text/event-stream",
				...authHeaders,
			},
			body: JSON.stringify({
				sessionId,
				examName,
				tenantName,
				outputLanguage,
				examId: `${examId}`,
				useSupabaseStore: true,
				appId,
				...(additionalContext ? { additionalContext } : {}),
			}),
		});
		if (!response.ok) {
			throw new Error(
				`Failed to fetch session analysis: ${response.status} ${response.statusText}`,
			);
		}
		return response;
	};

	const runLiveAnalysis = async (signal: AbortSignal) => {
		setLoading(true);
		setStreaming(false);
		try {
			const response = await postLiveAnalysis(signal);
			setLoading(false);
			setStreaming(true);
			await streamTextResponse(response, setSessionAnalysis, signal);
		} catch (error) {
			if (signal.aborted) {
				return;
			}
			console.error("Error fetching session analysis:", error);
			setSessionAnalysis("");
		} finally {
			if (!signal.aborted) {
				setLoading(false);
				setStreaming(false);
			}
		}
	};

	const runMockedStream = (signal: AbortSignal) => {
		setStreaming(true);
		const words = mockedSessionAnalysis[demoExamId as DemoExamsType].split(" ");
		const step = Platform.OS === "web" ? 1 : 4;
		let wordIndex = 0;
		const interval = setInterval(() => {
			if (wordIndex >= words.length) {
				clearInterval(interval);
				setStreaming(false);
				return;
			}
			wordIndex = Math.min(wordIndex + step, words.length);
			const currentText = words.slice(0, wordIndex).join(" ");
			startTransition(() => {
				setSessionAnalysis(currentText);
			});
		}, 5);
		signal.addEventListener("abort", () => clearInterval(interval));
	};

	const runDemoAnalysis = async (signal: AbortSignal) => {
		setLoading(true);
		const response = await expoFetch(DEMO_SESSION_ANALYSIS_ENDPOINT, {
			method: "POST",
			headers: {
				"Content-Type": "application/json",
				Accept: "text/event-stream",
			},
			body: JSON.stringify({
				sessionId,
				examName,
				tenantName,
				outputLanguage,
				//TODO: Make this dynamic
				agentId: 742,
				input: {
					goldStandard: goldStandardData,
					userData,
				},
			}),
		});
		setLoading(false);
		setStreaming(true);
		await streamTextResponse(response, setSessionAnalysis, signal);
		if (!signal.aborted) {
			setStreaming(false);
		}
	};

	// biome-ignore lint/correctness/useExhaustiveDependencies: // TODO fix me later
	const fetchSessionAnalysis = useCallback(
		(signal: AbortSignal) => {
			if (!sessionId) {
				return;
			}
			if (isLiveAnalysis) {
				return runLiveAnalysis(signal);
			}
			if (!isMediQuest) {
				return runMockedStream(signal);
			}
			return runDemoAnalysis(signal);
		},
		[sessionId, additionalContext],
	);

	useEffect(() => {
		// Defer the live fetch until upstream prerequisites (e.g. the reports
		// query that populates session.analysis.input) are ready. The card still
		// renders in its loading state meanwhile.
		if (!ready) {
			return;
		}
		const controller = new AbortController();
		fetchSessionAnalysis(controller.signal);
		return () => {
			controller.abort();
		};
	}, [fetchSessionAnalysis, ready]);
	return {
		sessionAnalysis,
		loading: (isLiveAnalysis && !ready) || loading,
		streaming,
	};
};
